import { CellContext } from "@tanstack/react-table";
import type { ViewTableFeatures } from "@design-system/ViewTable/core/table";
import { memo, useState, useCallback } from "react";
import { FinancialDocumentListItem } from "@common/types/financialDocument";
import { canRevertFinancialDocumentRename } from "@common/helpers/financialDocument";
import { RemovingDocumentErrorCode } from "@common/constants/financial-documents";
import { CaptureClicks } from "@design-system/CaptureClicks/CaptureClicks";
import { Menu } from "@design-system/Menu/Menu.lib";
import { ConfirmationModal } from "@design-system/Modal/ConfirmationModal";
import {
    useRemoveFinancialDocumentMutation,
    useUpdateFinancialDocumentMetadataMutation,
} from "@api/financialDocument.api";
import { useToaster } from "@design-system/ToastMessages/useToaster";
import tableStyles from "@design-system/ViewTable/ViewTable.module.scss";
import { Loader } from "@design-system/Loader/Loader";
import { FinancialDocumentRenameModal } from "../../FinancialDocumentRenameModal/FinancialDocumentRenameModal";
import { useDownloadFinancialDocumentFile } from "../../FinancialDocumentDetails/useDownloadFinancialDocumentFile";
import { useOpenFinancialDocumentsTask } from "../../useOpenFinancialDocumentsTask";
import { useIsFinancialDocumentsTableReadonly } from "../useIsFinancialDocumentsTableReadonly";

export const ActionsCell = memo(
    ({
        row,
        table,
    }: CellContext<ViewTableFeatures, FinancialDocumentListItem, unknown>) => {
        const document = row.original;
        const isTableReadonly = useIsFinancialDocumentsTableReadonly(table);
        const toaster = useToaster();
        const openFinancialDocumentsTask = useOpenFinancialDocumentsTask();
        const [numberOfTransactionsToRemove, setNumberOfTransactionsToRemove] =
            useState<number>();
        const [showRenameModal, setShowRenameModal] = useState(false);
        const [showDeleteConfirmation, setShowDeleteConfirmation] =
            useState(false);
        const removeDocumentMutation = useRemoveFinancialDocumentMutation({
            onError: (error) => {
                if (
                    "code" in error &&
                    error.code ===
                        RemovingDocumentErrorCode.REMOVING_DOCUMENT_USED_IN_RECONCILIATION
                ) {
                    const docName =
                        "documentNames" in error &&
                        Array.isArray(error.documentNames) &&
                        error.documentNames.length > 0
                            ? error.documentNames[0]
                            : null;
                    toaster.error(
                        docName ? (
                            <>
                                <strong>{docName}</strong> is used in a bank
                                reconciliation and cannot be deleted. Undo the
                                reconciliation first.
                            </>
                        ) : (
                            "This document is used in a bank reconciliation and cannot be deleted. Undo the reconciliation first."
                        ),
                    );
                    return;
                }
                if ("documents" in error && error.documents.length > 0) {
                    setNumberOfTransactionsToRemove(
                        error.documents[0].transactionsCount,
                    );
                }
            },
        });

        const handleRemoveClick = useCallback(() => {
            setShowDeleteConfirmation(true);
        }, []);

        const handleDeleteConfirmed = useCallback(() => {
            setShowDeleteConfirmation(false);
            removeDocumentMutation.mutate({
                financialDocumentId: document.id,
                optimisticUpdate: false,
            });
        }, [document, removeDocumentMutation]);

        const confirmRemove = useCallback(async () => {
            await removeDocumentMutation.mutateAsync({
                financialDocumentId: document.id,
                confirmTransactionsRemoval: true,
            });
            setNumberOfTransactionsToRemove(undefined);
        }, [document, removeDocumentMutation]);

        const handleRenameClick = useCallback(() => {
            setShowRenameModal(true);
        }, []);

        const handleRenameClose = useCallback(() => {
            setShowRenameModal(false);
        }, []);

        const updateMetadataMutation =
            useUpdateFinancialDocumentMetadataMutation(document);

        const canRevertRename = canRevertFinancialDocumentRename(document);

        const handleRevertRename = useCallback(() => {
            if (!canRevertFinancialDocumentRename(document)) {
                return;
            }
            updateMetadataMutation.mutate({
                fileName: document.originalFileName,
            });
        }, [document, updateMetadataMutation]);

        const handleCreateTask = useCallback(() => {
            openFinancialDocumentsTask({
                financialDocumentIds: [document.id],
                entityIds: document.entity?.id ? [document.entity.id] : [],
            });
        }, [document, openFinancialDocumentsTask]);

        const { download, isDownloading } =
            useDownloadFinancialDocumentFile(document);

        if (isTableReadonly) {
            return null;
        }

        return (
            <>
                {!removeDocumentMutation.isPending ? (
                    <CaptureClicks>
                        <Menu.More className={tableStyles.showOnHover}>
                            <Menu.Item onClick={handleCreateTask}>
                                Create task
                            </Menu.Item>
                            <Menu.Item onClick={handleRenameClick}>
                                Rename
                            </Menu.Item>
                            {canRevertRename && (
                                <Menu.Item onClick={handleRevertRename}>
                                    Restore original name
                                </Menu.Item>
                            )}
                            <Menu.Item
                                disabled={isDownloading}
                                onClick={download}
                            >
                                Download
                            </Menu.Item>
                            <Menu.Item destructive onClick={handleRemoveClick}>
                                Delete
                            </Menu.Item>
                        </Menu.More>
                    </CaptureClicks>
                ) : (
                    <Loader inline />
                )}

                <FinancialDocumentRenameModal
                    show={showRenameModal}
                    financialDocument={document}
                    onClose={handleRenameClose}
                />

                <ConfirmationModal
                    show={showDeleteConfirmation}
                    onHide={() => setShowDeleteConfirmation(false)}
                    closeButton
                    title="Delete document"
                    question={`Are you sure you want to delete "${document.fileName}"?`}
                    onConfirm={handleDeleteConfirmed}
                    onReject={() => setShowDeleteConfirmation(false)}
                    yes="Delete"
                    yesBtnVariant="destructive"
                    no="Cancel"
                />

                <ConfirmationModal
                    show={Boolean(numberOfTransactionsToRemove)}
                    onHide={() => setNumberOfTransactionsToRemove(undefined)}
                    closeButton
                    title={`Are you sure you want to remove ${numberOfTransactionsToRemove} imported transactions?`}
                    question={
                        <span className="text-danger">
                            Warning: This action cannot be undone.
                        </span>
                    }
                    onConfirm={confirmRemove}
                    onReject={() => setNumberOfTransactionsToRemove(undefined)}
                    yes="Remove"
                    yesBtnVariant="destructive"
                    no="Cancel"
                />
            </>
        );
    },
);
